'use client'

import React, { useEffect, useState } from 'react'
import { validatePhoneNumber } from '@/utils/phoneFormatter'
import { useForm } from 'react-hook-form'
import { useHookFormMask } from 'use-mask-input'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import validator from 'validator'
import submitForm from '@/utils/submitFormMail'
import styles from './styles.module.css'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  subtitle: string
  onSuccess?: () => void
  useFinRecipient?: boolean
}

const formSchema = z.object({
  name: z.string().optional(),
  phone: z
    .string()
    .min(1, {
      message: 'Необходимо заполнить поле'
    })
    .refine(
      val =>
        validator.isMobilePhone(
          val.slice(1).replaceAll('(', '').replaceAll(')', '').replaceAll(' ', '').replaceAll('-', ''),
          'ru-RU'
        ),
      {
        message: 'Поле Введено некоректно'
      }
    ),
  description: z.string().optional(),
  privacy: z.boolean().refine(val => val === true, {
    message: 'Необходимо согласиться'
  })
})

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, subtitle, onSuccess, useFinRecipient }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      phone: '',
      description: '',
      privacy: false
    }
  })
  
  const registerWithMask = useHookFormMask(form.register)

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true)
    
    try {
      const formData = new FormData()
      formData.set('name', values.name || '')
      formData.set('phone', values.phone)
      formData.set('description', values.description || '')
      formData.set('service_type', title === 'Подключить сервис?' ? 'TrueCodePay Service' : 'General Consultation')

      await submitForm(formData, { useFinRecipient })
      // Закрываем основной modal и открываем SuccessModal через onSuccess
      onClose()
      onSuccess?.()
      form.reset()
    } catch (error) {
      console.error('Ошибка отправки формы:', error)
      // При ошибке просто закрываем модалку
      onClose()
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Сбрасываем форму при закрытии модалки
  useEffect(() => {
    if (!isOpen) {
      form.reset()
    }
  }, [isOpen, form])


  // Управляем прокруткой при изменении isOpen
  // Управляем анимацией и прокруткой
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true)
      // Небольшая задержка для плавного появления
      setTimeout(() => {
        setIsAnimating(true)
      }, 10)
      document.body.style.overflow = 'hidden'
    } else {
      setIsAnimating(false)
      // Задержка перед скрытием для завершения анимации
      const timer = setTimeout(() => {
        setIsVisible(false)
      }, 300)
      // Не разблокируем скролл здесь - пусть SuccessModal управляет
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Обработчик ESC клавиши
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        // Убираем выделение с активного элемента
        const activeElement = document.activeElement
        if (activeElement && activeElement instanceof HTMLElement) {
          activeElement.blur()
        }
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!isVisible) return null

  return (
    <>
      {/* Backdrop with blur */}
      <div 
        className={`${styles.backdrop} ${isAnimating ? styles.show : ''}`}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={`${styles.modalContainer} ${isAnimating ? styles.show : ''}`}>
        <div 
          className={`${styles.modalContent} ${isAnimating ? styles.show : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <div className={styles.closeButtonContainer}>
            <button 
              className={styles.closeButton}
              onClick={onClose}
            >
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 20C0 8.95431 8.95431 0 20 0C31.0457 0 40 8.95431 40 20C40 31.0457 31.0457 40 20 40C8.95431 40 0 31.0457 0 20Z" fill="#F3F2F4"/>
                <path d="M24.6663 16.2734L23.7263 15.3334L19.9997 19.06L16.273 15.3334L15.333 16.2734L19.0597 20L15.333 23.7267L16.273 24.6667L19.9997 20.94L23.7263 24.6667L24.6663 23.7267L20.9397 20L24.6663 16.2734Z" fill="#151515"/>
              </svg>
            </button>
          </div>

          {/* Title */}
          <h2 className={styles.title}>
            {title}
          </h2>

          {/* Subtitle */}
          <p className={styles.subtitle}>
            {subtitle}
          </p>

          {/* Form */}
          <form onSubmit={form.handleSubmit(onSubmit)} className={styles.form} noValidate>
            {/* Name field */}
            <div className={styles.inputGroup}>
              <input 
                {...form.register('name')}
                type="text" 
                placeholder="Ваше имя"
                className={styles.inputField}
              />
            </div>
            
            {/* Phone field */}
            <div className={styles.inputGroup}>
              <div className={styles.inputContainer}>
                <input 
                  {...registerWithMask('phone', ['+7 (999) 999-99-99'])}
                  type="tel" 
                  placeholder="Телефон *"
                  className={styles.inputField}
                />
              </div>
              {form.formState.errors.phone && (
                <div className={styles.errorMessage}>{form.formState.errors.phone.message}</div>
              )}
            </div>
            
            {/* Description field */}
            <div className={styles.textareaGroup}>
              <textarea 
                {...form.register('description')}
                placeholder="Опишите задачу или проект"
                className={styles.textarea}
                rows={4}
              />
            </div>
            
            {/* Privacy policy checkbox */}
            <div className={`${styles.checkboxGroup} ${form.formState.errors.privacy ? styles.checkboxGroupError : ''}`}>
              <div className={styles.checkboxContainer}>
                <div className={styles.checkboxWrapper}>
                  <input 
                    {...form.register('privacy')}
                    type="checkbox" 
                    id="modal-privacy"
                    className={styles.checkboxInput}
                  />
                  <label htmlFor="modal-privacy" className={styles.checkboxLabel}>
                    {/* Unchecked state */}
                    <svg 
                      className={styles.checkboxUnchecked}
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M19 5V19H5V5H19ZM19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z" fill="black"/>
                    </svg>
                    {/* Checked state */}
                    <svg 
                      className={styles.checkboxChecked}
                      width="24" 
                      height="24" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM10.71 16.29C10.32 16.68 9.69 16.68 9.3 16.29L5.71 12.7C5.32 12.31 5.32 11.68 5.71 11.29C6.1 10.9 6.73 10.9 7.12 11.29L10 14.17L16.88 7.29C17.27 6.9 17.9 6.9 18.29 7.29C18.68 7.68 18.68 8.31 18.29 8.7L10.71 16.29Z" fill="#2F2F2F"/>
                    </svg>
                  </label>
                </div>
              </div>
              <label htmlFor="modal-privacy" className={styles.checkboxText}>
                Я ознакомился с <a href="/privacy.pdf" target="_blank" rel="noopener noreferrer" className={styles.privacyLink}>политикой конфиденциальности</a> и согласен на обработку персональных данных
              </label>
            </div>
            
            {/* Submit button */}
            <div className={styles.buttonGroup}>
              <button 
                type="submit" 
                className={styles.submitButton}
                disabled={isSubmitting}
              >
                <span className={styles.buttonText}>
                  {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

Modal.displayName = 'Modal'

export default Modal

